import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePopup } from '../../contexts/PopupContext';
import {
  ProfileSpotlightHeartIcon,
  ProfileStatClockIcon,
  ProfileStatTileIcon,
} from './ProfileActivityIcons';

/**
 * Widżet: kafelki statystyk + (opcjonalnie) ulubiony klimat w układzie 50/50.
 * Nawigacja: kafelki i przycisk w nagłówku przechodzą do odpowiednich widoków aplikacji.
 */
function ProfileStatsWidget({
  stats,
  statsLoading,
  onNavigateToMyEvents,
  onNavigateToDiscover,
}) {
  const { t } = useTranslation(['profile', 'events']);
  const { openPopup } = usePopup();

  const statTiles = useMemo(
    () => [
      {
        id: 'upcoming',
        value: stats.participations.upcoming,
        label: t('profile.dashboard.statUpcoming', 'Nadchodzące'),
        ariaKey: 'dashboard.statTileAriaUpcoming',
      },
      {
        id: 'completed',
        value: stats.participations.completed,
        label: t('profile.dashboard.statCompleted', 'Ukończone'),
        ariaKey: 'dashboard.statTileAriaCompleted',
      },
      {
        id: 'organized',
        value: stats.organized,
        label: t('profile.dashboard.statOrganized', 'Zorganizowane'),
        ariaKey: 'dashboard.statTileAriaOrganized',
      },
      {
        id: 'friends',
        value: stats.friendsCount,
        label: t('profile.dashboard.statFriends', 'Znajomi'),
        ariaKey: 'dashboard.statTileAriaFriends',
      },
      {
        id: 'places',
        value: stats.uniqueCities,
        label: t('profile.dashboard.statPlaces', 'Miejsca'),
        ariaKey: 'dashboard.statTileAriaPlaces',
      },
    ],
    [stats, t],
  );

  const handleGoMyEvents = useCallback(
    (subtab) => {
      onNavigateToMyEvents?.(subtab);
    },
    [onNavigateToMyEvents],
  );

  const handleStatTileClick = useCallback(
    (tileId) => {
      if (statsLoading) return;
      switch (tileId) {
        case 'upcoming':
          handleGoMyEvents('upcoming');
          break;
        case 'completed':
          handleGoMyEvents('history');
          break;
        case 'organized':
          handleGoMyEvents('upcoming');
          break;
        case 'friends':
          openPopup('friends-management', {}, 0);
          break;
        case 'places':
          onNavigateToDiscover?.();
          break;
        default:
          break;
      }
    },
    [statsLoading, handleGoMyEvents, openPopup, onNavigateToDiscover],
  );

  const showSpotlight = Boolean(stats.favoriteCategory) && !statsLoading;

  return (
    <section className="profile-stats-widget" aria-labelledby="p-stats-widget-heading">
      <div className="profile-dash-widget-card">
        <header className="profile-dash-widget-head profile-stats-widget-head">
          <h2 id="p-stats-widget-heading" className="profile-dash-widget-title">
            {t('profile.dashboard.statsSectionTitle', 'Aktywność')}
          </h2>
          {onNavigateToMyEvents && (
            <button
              type="button"
              className="profile-dash-header-action"
              onClick={() => handleGoMyEvents('upcoming')}
            >
              <span className="profile-dash-header-action-icon" aria-hidden>
                <ProfileStatClockIcon size={17} className="p-dash-stat-icon-svg" />
              </span>
              <span className="profile-dash-header-action-label">
                {t('dashboard.viewUpcomingButton', 'Nadchodzące wydarzenia')}
              </span>
            </button>
          )}
        </header>

        <div
          className={
            showSpotlight
              ? 'profile-stats-widget-body profile-stats-widget-body--split'
              : 'profile-stats-widget-body'
          }
        >
          <div className="profile-stats-widget-col profile-stats-widget-col--stats">
            <section
              className="p-dash-stats"
              aria-label={t('profile.dashboard.statsAria', 'Podsumowanie aktywności')}
            >
              {statsLoading ? (
                <div className="p-dash-stats-skeleton" aria-busy="true">
                  <div className="p-dash-stats-skel-cell p-dash-stats-skel-cell--wide" />
                  <div className="p-dash-stats-skel-cell p-dash-stats-skel-cell--wide" />
                  <div className="p-dash-stats-skel-cell p-dash-stats-skel-cell--compact" />
                  <div className="p-dash-stats-skel-cell p-dash-stats-skel-cell--compact" />
                  <div className="p-dash-stats-skel-cell p-dash-stats-skel-cell--compact" />
                </div>
              ) : (
                <ul className="p-dash-stat-grid">
                  {statTiles.map((tile) => (
                    <li
                      key={tile.id}
                      className={`p-dash-stat-tile-wrap p-dash-stat-tile-wrap--${tile.id}`}
                    >
                      <button
                        type="button"
                        className={`p-dash-stat-tile p-dash-stat-tile--${tile.id}`}
                        onClick={() => handleStatTileClick(tile.id)}
                        aria-label={t(tile.ariaKey, tile.label)}
                      >
                        <span className="p-dash-stat-copy">
                          <span className="p-dash-stat-value">{tile.value}</span>
                          <span className="p-dash-stat-label">{tile.label}</span>
                        </span>
                        <span className="p-dash-stat-tile-visual" aria-hidden>
                          <span className="p-dash-stat-icon">
                            <ProfileStatTileIcon tileId={tile.id} size={45} />
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          {showSpotlight && (
            <div className="profile-stats-widget-col profile-stats-widget-col--spotlight">
              <div className="p-dash-spotlight p-dash-spotlight--stats-widget">
                <span className="p-dash-spotlight-icon" aria-hidden>
                  <ProfileSpotlightHeartIcon size={24} />
                </span>
                <div className="p-dash-spotlight-body">
                  <p className="p-dash-spotlight-title">
                    {t('profile.dashboard.spotlightTitle', 'Ulubiony klimat wydarzeń')}
                  </p>
                  <p className="p-dash-spotlight-value">
                    {t(`events:categories.${stats.favoriteCategory}`, stats.favoriteCategory)}
                  </p>
                  <p className="p-dash-spotlight-hint">
                    {t(
                      'profile.dashboard.spotlightHint',
                      'Na podstawie kategorii Twoich ukończonych wydarzeń.',
                    )}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default ProfileStatsWidget;
